import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Wand2, Trash2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ImageManager } from '@/lib/imageManager';

interface ProductAIProps {
    productId: string;
    productName: string;
    simulations: string[];
    onUpdate: (newSimulations: string[]) => void;
}

const ROOM_TYPES = [
    { value: 'modern living room', label: 'סלון מודרני' },
    { value: 'rustic bedroom', label: 'חדר שינה כפרי' },
    { value: 'minimalist kitchen', label: 'מטבח מינימליסטי' },
    { value: 'luxury bathroom', label: 'חדר רחצה יוקרתי' },
    { value: 'home office', label: 'משרד ביתי' },
    { value: 'outdoor patio', label: 'מרפסת שמש' },
    { value: 'custom', label: 'אחר (הזן ידנית)' },
];

const PLACEMENT_OPTIONS = [
    { value: 'flooring', label: 'ריצוף (רצפה)' },
    { value: 'wall_cladding', label: 'חיפוי קיר' },
    { value: 'ceiling', label: 'תקרה' },
    { value: 'countertop', label: 'משטח עבודה / שיש' },
    { value: 'furniture', label: 'רהיט' },
    { value: 'standalone', label: 'עומד בפני עצמו' },
];

export function ProductAI({ productId, productName, simulations = [], onUpdate }: ProductAIProps) {
    const [loading, setLoading] = useState(false);
    const [roomType, setRoomType] = useState(ROOM_TYPES[0].value);
    const [customRoomType, setCustomRoomType] = useState('');
    const [placement, setPlacement] = useState(PLACEMENT_OPTIONS[1].value); // Default to wall cladding as it's common
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const promptContext = roomType === 'custom' ? customRoomType : roomType;

            if (roomType === 'custom' && !customRoomType.trim()) {
                toast.error('אנא הזן תיאור של סוג החדר');
                setLoading(false);
                return;
            }

            const { data, error } = await supabase.functions.invoke('generate-product-visualization', {
                body: {
                    product_id: productId,
                    prompt_context: promptContext,
                    placement: placement
                }
            });

            if (error) throw error;
            if (data.error) throw new Error(data.error);

            if (data.debug_analysis) {
                console.log('AI Analysis:', data.debug_analysis);
                toast.success(`ההדמיה נוצרה successfully! זוהה: ${data.debug_analysis.color_name || 'צבע לא ידוע'}`);
            } else {
                toast.success('ההדמיה נוצרה בהצלחה!');
            }
            onUpdate([...simulations, data.imageUrl]);
        } catch (error: any) {
            console.error('Error generating simulation:', error);

            let errorMessage = error.message || 'שגיאה לא ידועה';

            // Try to extract specific error from function response body
            if (error.context && typeof error.context.json === 'function') {
                try {
                    const errorData = await error.context.json();
                    if (errorData && errorData.error) {
                        errorMessage = errorData.error;
                        console.log('Parsed API error:', errorMessage);
                    }
                } catch (parseError) {
                    console.error('Error parsing error response:', parseError);
                }
            }

            toast.error(`שגיאה ביצירת ההדמיה: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (imageUrl: string) => {
        if (!confirm('האם אתה בטוח שברצונך למחוק הדמיה זו?')) return;

        try {
            // Filter out the image to delete
            const updatedSimulations = simulations.filter(img => img !== imageUrl);

            const { error } = await supabase
                .from('products')
                .update({ simulation_images: updatedSimulations })
                .eq('id', productId);

            if (error) throw error;

            toast.success('ההדמיה נמחקה');
            onUpdate(updatedSimulations);
        } catch (error) {
            console.error('Error deleting simulation:', error);
            toast.error('שגיאה במחיקת ההדמיה');
        }
    };

    return (
        <Card className="mt-6">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-600" />
                    יצירת תמונה בAI
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col md:flex-row gap-4 items-end mb-6">
                    <div className="flex-1 w-full relative">
                        <Label>מיקום המוצר</Label>
                        <Select value={placement} onValueChange={setPlacement}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PLACEMENT_OPTIONS.map(option => (
                                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex-1 w-full">
                        <Label>סוג חדר</Label>
                        <Select value={roomType} onValueChange={setRoomType}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROOM_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {roomType === 'custom' && (
                        <div className="flex-1 w-full">
                            <Label>תיאור החדר</Label>
                            <Input
                                value={customRoomType}
                                onChange={(e) => setCustomRoomType(e.target.value)}
                                placeholder="לדוגמה: מסעדה יוקרתית, חנות בגדים"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleGenerate();
                                    }
                                }}
                            />
                        </div>
                    )}

                    <Button
                        type="button"
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-purple-600 hover:bg-purple-700 text-white min-w-[150px]"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                                מעבד...
                            </>
                        ) : (
                            <>
                                <Wand2 className="w-4 h-4 ml-2" />
                                צור הדמיה
                            </>
                        )}
                    </Button>
                </div>

                {/* Gallery Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {simulations.map((img, index) => (
                        <div key={index} className="group relative aspect-square rounded-lg overflow-hidden border bg-muted">
                            <img
                                src={ImageManager.getImageUrl(img)}
                                alt={`AI Simulation ${index + 1}`}
                                className="w-full h-full object-cover cursor-pointer transition-transform hover:scale-105"
                                onClick={() => setSelectedImage(img)}
                                onError={(e) => ImageManager.handleImageError(e)}
                            />
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => handleDelete(img)}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 text-center">
                                הדמיית AI
                            </div>
                        </div>
                    ))}
                    {simulations.length === 0 && (
                        <div className="col-span-full text-center text-muted-foreground py-8">
                            עדיין לא נוצרו הדמיות למוצר זה
                        </div>
                    )}
                </div>
            </CardContent>

            <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
                <DialogContent className="max-w-4xl p-0 overflow-hidden border-0 bg-transparent shadow-none">
                    {selectedImage && (
                        <img
                            src={ImageManager.getImageUrl(selectedImage)}
                            alt="Simulation Fullscreen"
                            className="w-full h-auto rounded-lg shadow-2xl"
                            onError={(e) => ImageManager.handleImageError(e)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}
